/**
 * @file Dashboard overview (Material UI).
 */
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Box, Button, Paper, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";

interface OverviewStats {
  totalGuides: number;
  totalChapters: number;
  totalPages: number;
  inReview: number;
  draft: number;
  published: number;
}

function StatCard({
  title,
  value,
  href,
  cardSx,
}: {
  title: string;
  value: number;
  href: string;
  cardSx: SxProps<Theme>;
}) {
  return (
    <Paper
      component={Link}
      to={href}
      sx={{
        p: 3,
        flex: "1 1 160px",
        minWidth: 160,
        textDecoration: "none",
        color: "inherit",
        border: 1,
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 2,
        },
        ...cardSx,
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
        {title}
      </Typography>
      <Typography variant="h3" fontWeight={700}>
        {value}
      </Typography>
    </Paper>
  );
}

export function DashboardHomePage() {
  const [stats, setStats] = useState<OverviewStats | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const overview = await apiFetch<OverviewStats>("/api/v1/analytics/overview");
        setStats(overview);
      } catch {
        setStats({
          totalGuides: 0,
          totalChapters: 0,
          totalPages: 0,
          inReview: 0,
          draft: 0,
          published: 0,
        });
      }
    })();
  }, []);

  return (
    <Box>
      <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
        Overview
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 560 }}>
        Manage chapters, pages, reviews, and publishing from the navigation drawer.
      </Typography>

      {stats && (
        <Box display="flex" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
          <StatCard
            title="Guides"
            value={stats.totalGuides}
            href="/dashboard/guides"
            cardSx={{
              borderColor: "info.main",
              bgcolor: (t) => alpha(t.palette.info.main, 0.1),
            }}
          />
          <StatCard
            title="Total chapters"
            value={stats.totalChapters}
            href="/dashboard/chapters?guideId=all"
            cardSx={{
              borderColor: "primary.main",
              bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
            }}
          />
          <StatCard
            title="Total pages"
            value={stats.totalPages}
            href="/dashboard/pages"
            cardSx={{
              borderColor: "secondary.main",
              bgcolor: (t) => alpha(t.palette.secondary.main, 0.12),
            }}
          />
          <StatCard
            title="In review"
            value={stats.inReview}
            href="/dashboard/pages?reviewQueue=1"
            cardSx={{
              borderColor: "warning.main",
              bgcolor: (t) => alpha(t.palette.warning.main, 0.15),
            }}
          />
          <StatCard
            title="Draft pages"
            value={stats.draft}
            href="/dashboard/pages?status=draft"
            cardSx={{
              borderColor: "grey.400",
              bgcolor: (t) => alpha(t.palette.grey[500], 0.12),
            }}
          />
          <StatCard
            title="Published pages"
            value={stats.published}
            href="/dashboard/pages?publishedLive=1"
            cardSx={{
              borderColor: "success.main",
              bgcolor: (t) => alpha(t.palette.success.main, 0.12),
            }}
          />
        </Box>
      )}

      <Button component={Link} to="/dashboard/pages" variant="outlined" endIcon={<ArrowForwardIcon />}>
        Go to pages
      </Button>
    </Box>
  );
}
